import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import { ColorValue, DrawingOptions, DrawingSettingGroup, DrawingTypes, SettingValues } from '../drawing.interface'
import { SettingType } from '../drawing.enums'
import { Coordinate, Time } from 'lightweight-charts'
import { GetSeriesResponse_Series } from '../../../../proto/generated/candle-data-depot'
import { utcToIST } from '../../../../util/dateTime'
import { callColorCumulative, putColorCumulative } from '../../../multiStikeChart/styles'
import { ChartEvents } from '../../chart.events'
import { OiHeatmapTooltipData } from '../../oiProfileTooltip/oiProfileTooltip'

const BOX_WIDTH = 40;
const SPACING = 40;

const MAX_CANDLES = 20;

export type OiHeatmapData = {
    series: GetSeriesResponse_Series[]
}

export type OIHeatmapCandle = {
    timestamp: number,
    change: Record<number, {
        call: number,
        put: number
    }>
}

export type OIHeatmapState = {
    breakpoints: number[],
    data: Record<number, OIHeatmapCandle[]>,
    rollupFactor: number
}

export type OiHeatmapSettings = {
    callChangeColor: ColorValue,
    putChangeColor: ColorValue,
    data: OiHeatmapData,
    indicator: boolean,
}

class OiHeatmapDrawing extends DrawingBase<any, OiHeatmapSettings, OIHeatmapState> {

    constructor(options: DrawingOptions<any, OiHeatmapSettings, OIHeatmapState>) {
        super(options)
        this._state.data = {};
        this.addMouseMoveHandlerHeatmap();
        setTimeout(() => {
            this.generateDrawingData();
            this.primitive?.requestUpdate()
        }, 100)
    }

    draw = (scope: BitmapCoordinatesRenderingScope) => {
        const ctx = scope.context
        const {data} = this._state;
        
        if (data  && data[1]) {
            const {rangeStart, rangeEnd, startTime, endTime} = this._chart.viewportCandleInfo;
            const numCandles = rangeEnd - rangeStart + 1;

            const maxCandles = Math.min(this.canvasWidth/((BOX_WIDTH + SPACING)*window.devicePixelRatio), MAX_CANDLES)
            let rollupFactor = Math.ceil(numCandles/maxCandles)
            rollupFactor = Math.ceil(rollupFactor/2)*2
            if (!data[rollupFactor]) {
                const aggregatedData = this.aggregateCandles(rollupFactor, 1, data[1])
                this._state.data[rollupFactor] = aggregatedData;
            }
            this._state.rollupFactor = rollupFactor;
            const [startIndex, endIndex] = this.findVisibleRangeData(startTime as number, endTime as number, data[rollupFactor])

            const slicedData = data[rollupFactor].slice(startIndex, endIndex + 1);

            let maxChange = -Infinity;
            slicedData.forEach((c) => {
                maxChange = Math.max(maxChange, ...Object.values(c.change).map(v => [Math.abs(v.call), Math.abs(v.put)]).flat())
            })
            slicedData.forEach((c) => {
                const {timestamp, change} = c;
                const x = this.timeToCoordinate(timestamp as Time)
                if (!x || !Object.keys(change).length) return;
                const scaledX = x*devicePixelRatio

                for (let strike in change) {
                    const {call, put} = change[strike]
                    const y = this.priceToCoordinate(Number(strike)/100)
                    if (!y) continue
                    const scaledY = y*devicePixelRatio
                    const absCall = Math.abs(call), absPut = Math.abs(put)
                    let outerValue = call/maxChange, outerColor = this._settings.callChangeColor,
                        innerValue = put/maxChange, innerColor = this._settings.putChangeColor;
                    if (absCall < absPut) {
                        outerValue = put/maxChange;
                        outerColor = this._settings.putChangeColor
                        innerValue = call/maxChange
                        innerColor = this._settings.callChangeColor
                    }

                    if (Math.max(Math.abs(outerValue), Math.abs(innerValue)) < 0.2) continue
                    const outerSolid = outerValue > 0;
                    const innerSolid = innerValue > 0;
                    const outerWidth = Math.abs(outerValue*BOX_WIDTH)*devicePixelRatio
                    const innerWidth = Math.abs(innerValue*BOX_WIDTH)*devicePixelRatio
        
                    ctx.beginPath()
                    ctx.globalAlpha = outerColor.opacity;
                    if (outerSolid) {
                        ctx.fillStyle = outerColor.color;
                        ctx.fillRect((scaledX - outerWidth/2), (scaledY - outerWidth/2), outerWidth, outerWidth)
                    } else {
                        ctx.strokeStyle = outerColor.color;
                        ctx.strokeRect((scaledX - outerWidth/2), (scaledY - outerWidth/2), outerWidth, outerWidth)
                    }

                    if (innerSolid) {
                        ctx.fillStyle = innerColor.color;
                        ctx.fillRect((scaledX - innerWidth/2), (scaledY - innerWidth/2), innerWidth, innerWidth)
                    } else {
                        ctx.strokeStyle = innerColor.color;
                        ctx.strokeRect((scaledX - innerWidth/2), (scaledY - innerWidth/2), innerWidth, innerWidth)
                    }
                    ctx.closePath()
                }
            });
        }
        
    }
    

    getBaseLevel(level: number) {
        const {data} = this._state;
        const levels = Object.keys(data).map(v => parseInt(v))
        let c = levels.length - 1;
        while (c >= 0) {
            const l = levels[c]
            if (l !== level && level%l === 0) {
                return l;
            }
            c-- 
        }
        return 1
    }

    findVisibleRangeData = (startTime: number, endTime: number, data: OIHeatmapCandle[]): [number, number] => {
        const start = this.findIndexByTime(startTime, data)
        const end = this.findIndexByTime(endTime, data)
        return [ start, end];
    }

    findIndexByTime = (timeInSeconds: number, data: OIHeatmapCandle[]) => {
        let left = 0, right = data.length - 1
        if (!data.length) return 0

        const last = data[data.length - 1]
        if (last.timestamp < timeInSeconds) return data.length - 1;
        while (true) {
            const mid = Math.floor((left + right) / 2)
            if (left == right) {
                return left
            } else if (left == mid) {
                if (data[right].timestamp as number === timeInSeconds) {
                    return right
                } else {
                    return left
                }
            }
            const midCandle = data[mid]
            
            if ((midCandle.timestamp) == timeInSeconds) {
                return mid
            } else if ((midCandle.timestamp) < timeInSeconds) {
                left = mid
            } else {
                right = mid
            }
        }
    }

    generateDrawingData() {
        const {data} = this._settings
        const primaryData = this._chart.getRenderedPrimaryData()

        if (!data || !data.series || !primaryData.length) return;
        const {series} = data
        
        const candles: Record<number, OIHeatmapCandle> = {}

        primaryData.forEach((c) => {
            if (candles[c.time as number]) return;
            candles[c.time as number] = {
                timestamp: c.time as number,
                change: {}
            }
        })

        series.forEach((s) => {
            s.dataPoints.forEach(({timestamp, values}) => {
                if (s.seriesName === "breakpoints") return;
                const utcTimestamp = utcToIST(timestamp/1000)
                if (!candles[utcTimestamp]) {
                    return;
                };
                const candle: OIHeatmapCandle = candles[utcTimestamp] || {
                    timestamp: utcTimestamp,
                    change: {}
                }
                const callChange = values[0] || 0, putChange = values[1] || 0
                candle.change[Number(s.seriesName)] = {
                    call: callChange,
                    put: putChange
                }

                candles[utcTimestamp] = candle
            })
        })

        this._state.data =  {
            1: Object.keys(candles).map(v => parseInt(v)).sort((a,b) => a-b).map(v => candles[v])
        }

    }

    aggregateCandles = (level: number, baseLevel: number, baseData?: OIHeatmapCandle[]) => {

        const candles = baseData || this._state.data[baseLevel] || [];
        if (candles.length === 0) return [];
        let runningCandle: OIHeatmapCandle = {
            timestamp: 0,
            change: {}
        }
        let counter = 0;
        const factor = level/baseLevel;

        const data: OIHeatmapCandle[] = [];
        candles.forEach((candle) => {
            if (counter === 0 ) {
                if (runningCandle.timestamp) {
                    data.push(runningCandle)
                }
                runningCandle = {
                    timestamp: candle.timestamp,
                    change: {...candle.change}
                }
                counter = factor - 1
                return;
            }

            runningCandle.change = this.mergeCandles(runningCandle.change, candle.change, true)
            counter--;
        })

        runningCandle.timestamp && data.push(runningCandle)

        return data;
    }

    mergeCandles = (a: OIHeatmapCandle['change'], b: OIHeatmapCandle['change'], union = false): OIHeatmapCandle['change'] => {
        
        const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]))
        const newChange: OIHeatmapCandle['change'] = {}

        keys.forEach((k) => {
            const key = Number(k)

            if (!union && (!a[key] || !b[key])) return;
            newChange[key] = {
                call: (a[key]?.call ?? 0) + (b[key]?.call ?? 0),
                put: (a[key]?.put ?? 0) + (b[key]?.put ?? 0)
            }
        })

        return newChange
    }

    addMouseMoveHandlerHeatmap() {
        document.addEventListener("mousemove", this.onMouseMoveHeatmap, {capture: true})
    }

    onMouseMoveHeatmap = (e: MouseEvent) => {
        if (!this._chartBounds) return;

        const {rangeStart, rangeEnd} = this._chart.viewportCandleInfo;
        const numCandles = rangeEnd - rangeStart + 1;

        const candleWidth = this.canvasWidth/numCandles;
        const tooltipData: OiHeatmapTooltipData = {
            x: 0,
            y: 0,
            settings: this._settings,
            type: "oiHeatmap",
            strikes: []
        }
        const  {rollupFactor, data} = this._state
        if (!rollupFactor || !data[rollupFactor]) return;

        const target = e.target as HTMLElement;
        let parent = target.parentNode;
        while (parent) {
            if (parent === this._paneElement) break;
            parent = parent.parentNode;
        }
        if (parent) {
            const eventX = e.clientX;
            const eventY = e.clientY;
            const x = eventX - this._chartBounds.left;
            const y = eventY - this._chartBounds.top;
            const time = this.coordinateToTime(x as Coordinate);
            if (time) {
                const hoveredTime = time as number;
                const seriesData = data[rollupFactor];
                const index = this.findIndexByTime(time as number, seriesData)
                let t1 = seriesData[index].timestamp, t2 = seriesData[index + 1]?.timestamp ?? Infinity;
                let nearestIndex = t2 - hoveredTime < hoveredTime - t1 ? index + 1 : index;

                tooltipData.x = x;
                tooltipData.y = y;
                const changes = seriesData[nearestIndex].change;
                const x2 = this.timeToCoordinate(seriesData[nearestIndex].timestamp as Time)
                
                if (x2) {
                    let selectedStrike = 0;
                    const distance = Math.abs(x2 - x)*devicePixelRatio;
                    if (distance <= candleWidth/2) {
                        tooltipData.strikes = Object.keys(changes).map((strike) => {
                            const str = Number(strike)
                            if (!selectedStrike && this._chart.securities.length) {
                                const priceMultiplier = this._chart.securities[0].priceMultiplier || 100
                                const strikeCoord = this.priceToCoordinate(str/priceMultiplier);
                                if (strikeCoord && Math.abs(y - strikeCoord) <= 10*devicePixelRatio) {
                                    selectedStrike = str;
                                }
                            }
                            return {
                                strike: str,
                                ...changes[str]
                            }
                        })
                    }
                    tooltipData.selectedStrike = selectedStrike;
                   
                }

            }
            
        }
        this._chart.publishEvent(ChartEvents.DRAWING_HOVERED, tooltipData)
    }


    onSettingsUpdate = (settings: Partial<OiHeatmapSettings>) => {
        if (this._settings.data) {
            this.generateDrawingData();
        }
    }

    getAvailableSettings = (): DrawingSettingGroup<OiHeatmapSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'callChangeColor',
                    label: 'Call Change Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: callColorCumulative,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'putChangeColor',
                    label: 'Put Change Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: putColorCumulative,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): OiHeatmapSettings {
        return { ...this._settings }
    }

    getDrawingType(): DrawingTypes {
        return 'oiHeatmap'
    }

    getMiniSettings = () => {
        return ['callChangeColor', 'putChangeColor'] as (keyof OiHeatmapSettings)[]
    }
 
    destroy() {
        this._chart.publishEvent(ChartEvents.DRAWING_HOVERED, {
            x: 0,
            y: 0,
            settings: this._settings,
            type: "oiHeatmap",
            strikes: []
        })
        document.removeEventListener("mousemove", this.onMouseMoveHeatmap, {capture: true})
        super.destroy()
    }
}

export default OiHeatmapDrawing